import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { API } from "@/lib/api";
import { productMatches, searchWords } from "@/lib/search";
import { eur, num, showMoney } from "@/lib/format";
import { BomEditor } from "@/components/BomEditor";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Loader2, Package, Store, Settings, ListChecks, RefreshCw, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, AlertTriangle, Ruler, History } from "lucide-react";
import { toast } from "sonner";
import ExportMenu from "@/components/ExportMenu";
import { SyncStatus } from "@/components/SyncStatus";
import { CareSymbolsView } from "@/components/CareSymbols";
import { ProductIssues } from "@/components/ProductIssues";
import { ProducedQtyMenu } from "@/components/ProducedQtyMenu";
import { ProducedQtyEdit } from "@/components/ProducedQtyEdit";
import { SizeChartDialog } from "@/components/SizeChartDialog";
import { EntityHistory } from "@/components/EntityHistory";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const CURRENT_SEASON = "P27";


const PAGE_SIZE = 50;

function SizeGroupsCard({ groups }) {
  const total = groups.reduce((a, g) => a + g.value, 0) || 1;
  const soldTotal = groups.reduce((a, g) => a + (g.sold || 0), 0) || 1;
  return (
    <Card className="p-5 md:col-span-2 lg:col-span-3" data-testid="stats-size-groups">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">Per gruppi di taglie · magazzino e venduto</p>
        <p className="flex items-center gap-3 text-[11px] text-muted-foreground"><span className="flex items-center gap-1"><span className="h-2 w-3 rounded-full bg-primary" />In magazzino</span><span className="flex items-center gap-1"><span className="h-2 w-3 rounded-full bg-sky-500" />Venduto</span></p>
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {groups.map((g) => {
          const pct = Math.round((g.value / total) * 1000) / 10;
          const spct = Math.round(((g.sold || 0) / soldTotal) * 1000) / 10;
          const gap = Math.round((pct - spct) * 10) / 10;
          return (
            <div key={g.name} data-testid={`stats-size-group-${g.name}`}>
              <span className="text-sm font-medium">{g.name}</span>
              <div className="mt-1.5 space-y-1.5">
                <div className="flex items-center gap-2"><div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} /></div><span className="w-28 text-right text-xs"><b>{pct}%</b> · {num(g.value)} pz</span></div>
                <div className="flex items-center gap-2"><div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-sky-500" style={{ width: `${spct}%` }} /></div><span className="w-28 text-right text-xs"><b>{spct}%</b> · {num(g.sold || 0)} pz</span></div>
              </div>
              {Math.abs(gap) >= 3 && <p className={`mt-1 text-[11px] ${gap > 0 ? "text-amber-400" : "text-rose-400"}`} data-testid={`stats-size-gap-${g.name}`}>{gap > 0 ? `Magazzino più pieno di quanto vendi (+${gap} punti)` : `Vendi più di quanto hai in magazzino (${gap} punti)`}</p>}
              <div className="mt-2 flex flex-wrap gap-1">
                {g.sizes.map((s) => <span key={s.size} className="rounded bg-secondary px-1.5 py-0.5 font-mono-erp text-[11px]" title="in magazzino / venduto">{s.size}: {num(s.value)} / {num(s.sold || 0)}</span>)}
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-[11px] text-muted-foreground">Venduto: pezzi della stagione scelta venduti negli ordini, al netto dei resi. Sotto ogni gruppo: taglia → in magazzino / venduto.</p>
    </Card>
  );
}

function StatCard({ title, data, testid, values }) {
  const [open, setOpen] = useState(false);
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const shown = open ? entries : entries.slice(0, 10);
  const money = values && showMoney();
  const totPz = entries.reduce((s, [, v]) => s + (v > 0 ? v : 0), 0);
  const totVal = money ? Object.values(values).reduce((s, v) => s + (v > 0 ? v : 0), 0) : 0;
  const pct = (v, t) => `${t ? ((Math.max(v, 0) / t) * 100).toFixed(1).replace(".", ",") : "0"}%`;
  return (
    <Card className="p-5" data-testid={testid}>
      <p className="font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground mb-3">{title}</p>
      <div className="space-y-2">
        {shown.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{k}</span>
            <span className="font-semibold text-right">
              {num(v)} pz <span className="font-normal text-muted-foreground" data-testid={`${testid}-pct-${k}`}>({pct(v, totPz)})</span>
              {money && <span className="ml-2 font-normal text-muted-foreground" data-testid={`${testid}-value-${k}`}>· {eur(values[k] || 0)} ({pct(values[k] || 0, totVal)})</span>}
            </span>
          </div>
        ))}
      </div>
      {entries.length > 10 && (
        <Button variant="ghost" size="sm" className="mt-2 h-7 w-full text-xs" onClick={() => setOpen(!open)} data-testid={`${testid}-toggle`}>
          {open ? <>Mostra meno<ChevronUp className="ml-1 h-3.5 w-3.5" /></> : <>Mostra tutte ({entries.length})<ChevronDown className="ml-1 h-3.5 w-3.5" /></>}
        </Button>
      )}
    </Card>
  );
}

export default function Magazzino() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("tutte");
  const [chartProduct, setChartProduct] = useState(null);
  const [seasonFilter, setSeasonFilter] = useState(CURRENT_SEASON);
  const [colorFilter, setColorFilter] = useState("tutte");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("name-asc");
  const [lowOnly, setLowOnly] = useState(false);
  useEffect(() => { setPage(1); }, [search, catFilter, seasonFilter, colorFilter, sortBy, lowOnly]);
  const [statsSeason, setStatsSeason] = useState(CURRENT_SEASON);
  const [shopOpen, setShopOpen] = useState(false);
  const [shopDomain, setShopDomain] = useState("xn4mnw-dh.myshopify.com");
  const [bomProduct, setBomProduct] = useState(null);
  const [bomRows, setBomRows] = useState([]);

  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await api.get("/products")).data,
  });
  const { data: stats } = useQuery({
    queryKey: ["product-stats", statsSeason],
    queryFn: async () => (await api.get("/products/stats", { params: { season: statsSeason } })).data,
    placeholderData: (prev) => prev,
  });
  const { data: materials = [] } = useQuery({
    queryKey: ["materials"],
    queryFn: async () => (await api.get("/materials")).data,
  });
  const { data: shopCfg } = useQuery({
    queryKey: ["shopify-config"],
    queryFn: async () => (await api.get("/shopify/config")).data,
  });
  const { data: syncStatus } = useQuery({
    queryKey: ["sync-status"],
    queryFn: async () => (await api.get("/shopify/sync-status")).data,
    refetchInterval: (q) => {
      const d = q.state.data;
      return d?.products?.running || d?.orders?.running ? 3000 : 20000;
    },
  });
  const prodRunning = !!syncStatus?.products?.running;

  const syncShopify = useMutation({
    mutationFn: async (current) => (await api.post("/shopify/import-products", null, { params: current ? { season: "current" } : {} })).data,
    onSuccess: (d) => {
      if (d.status === "started") {
        toast.success(d.season ? `Sincronizzazione prodotti ${d.season} avviata…` : "Sincronizzazione completa prodotti avviata. Si aggiornano tra pochi istanti…");
        qc.invalidateQueries({ queryKey: ["sync-status"] });
        setTimeout(() => qc.invalidateQueries({ queryKey: ["sync-status"] }), 1500);
        setTimeout(() => { qc.invalidateQueries({ queryKey: ["products"] }); qc.invalidateQueries({ queryKey: ["product-stats"] }); }, 10000);
        setTimeout(() => { qc.invalidateQueries({ queryKey: ["products"] }); qc.invalidateQueries({ queryKey: ["product-stats"] }); }, 30000);
      } else {
        qc.invalidateQueries({ queryKey: ["products"] });
        qc.invalidateQueries({ queryKey: ["product-stats"] });
        toast.info(`${d.imported} prodotti demo caricati. Collega Shopify per i prodotti reali.`);
      }
    },
    onError: () => toast.error("Errore durante la sincronizzazione da Shopify"),
  });

  const connectShopify = () => {
    const d = (shopDomain || (shopCfg?.connected ? shopCfg.store_domain : "")).replace("https://", "").replace("http://", "").replace(/\/$/, "").trim();
    if (!d.endsWith(".myshopify.com")) {
      toast.error("Inserisci un dominio valido tipo xxx.myshopify.com");
      return;
    }
    window.location.href = `${API}/shopify/oauth/install?shop=${encodeURIComponent(d)}`;
  };

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("shopify") === "connected") {
      toast.success("Negozio Shopify collegato con successo");
      qc.invalidateQueries({ queryKey: ["shopify-config"] });
      window.history.replaceState({}, "", "/magazzino");
    }
  }, [qc]);

  const saveBom = useMutation({
    mutationFn: async ({ id, bom }) => (await api.patch(`/products/${id}`, { bom })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["products"] });
      toast.success("Distinta base salvata");
      setBomProduct(null);
    },
  });

  const openBom = async (p) => {
    const byId = Object.fromEntries(materials.map((m) => [m.id, m]));
    let src = p.bom || [];
    if (!src.length) {
      const s = (await api.get("/model-sheets/bom-for-product", { params: { sku: p.sku } }).catch(() => ({ data: { rows: [] } }))).data;
      src = s.rows || [];
      if (src.length) toast.info(`Distinta presa dalla scheda tecnica "${s.sheet_name}": controllala e salva`);
    }
    setBomRows(src.map((b) => {
      const m = byId[b.material_id];
      return { material_id: b.material_id, fused: !!b.fused, material_name: m?.name || b.material_name || "Materiale eliminato", variant: m ? [m.color, m.supplier].filter(Boolean).join(" · ") : "", unit: m?.unit || b.unit, qpu: String(b.quantity_per_unit ?? "") };
    }));
    setBomProduct(p);
  };

  const submitBom = () => {
    const bom = bomRows
      .filter((r) => Number(r.qpu) > 0)
      .map((r) => ({ material_id: r.material_id, fused: !!r.fused, material_name: r.material_name, unit: r.unit, quantity_per_unit: Number(r.qpu) }));
    saveBom.mutate({ id: bomProduct.id, bom });
  };

  const stockOf = (p) => p.sizes.reduce((s, x) => s + x.quantity, 0);

  const options = useMemo(() => {
    const cat = new Set(), sea = new Set(), col = new Set();
    products.forEach((p) => {
      if (p.season) sea.add(p.season);
      if (seasonFilter !== "tutte" && p.season !== seasonFilter) return;
      if (p.category) cat.add(p.category);
      if (p.color && p.color !== "N/D") col.add(p.color);
    });
    const sort = (s) => Array.from(s).sort((a, b) => a.localeCompare(b, "it"));
    return { categories: sort(cat), seasons: sort(sea), colors: sort(col) };
  }, [products, seasonFilter]);

  useEffect(() => {
    if (catFilter !== "tutte" && !options.categories.includes(catFilter)) setCatFilter("tutte");
    if (colorFilter !== "tutte" && !options.colors.includes(colorFilter)) setColorFilter("tutte");
  }, [options, catFilter, colorFilter]);

  const filtered = useMemo(() => {
    const words = searchWords(search);
    const list = products.filter((p) => {
      const matchS = productMatches(p, words);
      const matchC = catFilter === "tutte" || p.category === catFilter;
      const matchSe = seasonFilter === "tutte" || p.season === seasonFilter;
      const matchCol = colorFilter === "tutte" || p.color === colorFilter;
      const matchLow = !lowOnly || (p.shop_status !== "draft" && stockOf(p) <= (p.min_stock || 5));
      return matchS && matchC && matchSe && matchCol && matchLow;
    });
    const [key, dir] = sortBy.split("-");
    const val = { stock: stockOf, price: (p) => p.price || 0, name: (p) => p.name || "" }[key];
    const sign = dir === "desc" ? -1 : 1;
    return [...list].sort((a, b) => {
      const x = val(a), y = val(b);
      return sign * (typeof x === "string" ? x.localeCompare(y) : x - y);
    });
  }, [products, search, catFilter, seasonFilter, colorFilter, sortBy, lowOnly]);

  return (
    <Tabs defaultValue="prodotti" data-testid="products-tabs">
      <PageHeader title="Prodotti" subtitle="Prodotti finiti sincronizzati da Shopify (sola lettura). Le giacenze e le modifiche si gestiscono su Shopify.">
        <div className="flex flex-col items-end gap-2">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <TabsList>
              <TabsTrigger value="prodotti" data-testid="products-tab-list">Prodotti</TabsTrigger>
              <TabsTrigger value="statistiche" data-testid="products-tab-stats">Statistiche</TabsTrigger>
            </TabsList>
            <ProducedQtyMenu season={seasonFilter} />
            <ExportMenu base="magazzino" testid="export-magazzino" />
            <Button variant="outline" onClick={() => setShopOpen(true)} data-testid="shopify-config-btn">
              <Settings className="mr-2 h-4 w-4" />Shopify
            </Button>
            <div className="flex">
              <Button onClick={() => syncShopify.mutate(true)} disabled={syncShopify.isPending || prodRunning} className="rounded-r-none" data-testid="shopify-import-btn">
                {(syncShopify.isPending || prodRunning) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Sincronizza stagione {CURRENT_SEASON}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button disabled={syncShopify.isPending || prodRunning} className="rounded-l-none border-l border-primary-foreground/20 px-2" data-testid="product-sync-options-btn">
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => syncShopify.mutate(true)} data-testid="product-sync-season-item">Solo stagione corrente ({CURRENT_SEASON})</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => syncShopify.mutate(false)} data-testid="product-sync-full-item">Sincronizzazione completa (tutte le stagioni)</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <SyncStatus state={syncStatus?.products} testid="sync-status-products" />
        </div>
      </PageHeader>

        <TabsContent value="statistiche">
      <ProductIssues />
      {stats && (
        <div className="mb-3 flex items-center gap-3">
          <span className="font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">Statistiche stagione</span>
          <Select value={statsSeason} onValueChange={setStatsSeason}>
            <SelectTrigger className="w-44" data-testid="stats-season-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutte">Tutte le stagioni</SelectItem>
              {(stats.seasons || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {stats && (
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card className="flex flex-col p-5 bg-primary/5 border-primary/20">
            <p className="font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Totale a magazzino</p>
            <p className="font-display text-3xl font-bold" data-testid="stats-total-pieces">{num(stats.total_pieces)} pz</p>
            <p className="text-xs text-muted-foreground mt-1">{num(stats.total_skus)} SKU{showMoney() && ` · ${eur(stats.total_value)}`}</p>
            <div className="mt-5 border-t border-primary/20 pt-4" data-testid="stats-season">
              <p className="font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Per stagione</p>
              <div className="space-y-2">
                {Object.entries(stats.by_season || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{k}</span>
                    <span className="font-semibold">{num(v)} pz</span>
                  </div>
                ))}
              </div>
            </div>
          </Card>
          <StatCard title="Per categoria" data={stats.by_category} values={stats.value_by_category} testid="stats-category" />
          <StatCard title="Per colore" data={stats.by_color} values={stats.value_by_color} testid="stats-color" />
          {stats.size_groups?.length > 0 && <SizeGroupsCard groups={stats.size_groups} />}
        </div>
      )}
        </TabsContent>

        <TabsContent value="prodotti">
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Cerca nome o codice (es. 8001 a26 nero)..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-9 sm:w-[480px]" data-testid="product-search" />
        </div>
        <Select value={seasonFilter} onValueChange={setSeasonFilter}>
          <SelectTrigger className="w-40" data-testid="filter-season"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="tutte">Tutte le stagioni</SelectItem>{options.seasons.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-44" data-testid="filter-category"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="tutte">Tutte le categorie</SelectItem>{options.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={colorFilter} onValueChange={setColorFilter}>
          <SelectTrigger className="w-40" data-testid="filter-color"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="tutte">Tutti i colori</SelectItem>{options.colors.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-52" data-testid="product-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="name-asc">Nome A → Z</SelectItem>
            <SelectItem value="name-desc">Nome Z → A</SelectItem>
            <SelectItem value="stock-asc" data-testid="sort-stock-asc">Giacenza: dalla più bassa</SelectItem>
            <SelectItem value="stock-desc">Giacenza: dalla più alta</SelectItem>
            <SelectItem value="price-asc">Prezzo: dal più basso</SelectItem>
            <SelectItem value="price-desc">Prezzo: dal più alto</SelectItem>
          </SelectContent>
        </Select>
        <Button variant={lowOnly ? "default" : "outline"} size="sm" onClick={() => setLowOnly(!lowOnly)}
          className={lowOnly ? "bg-rose-600 text-white hover:bg-rose-700" : ""} data-testid="filter-low-stock">
          <AlertTriangle className="mr-1.5 h-3.5 w-3.5" />Solo sotto scorta
        </Button>
        {(catFilter !== "tutte" || seasonFilter !== CURRENT_SEASON || colorFilter !== "tutte" || search || lowOnly) && (
          <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setCatFilter("tutte"); setSeasonFilter(CURRENT_SEASON); setColorFilter("tutte"); setLowOnly(false); }} data-testid="filter-reset">
            Azzera filtri · {filtered.length} risultati
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex h-60 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((p) => {
            const stock = stockOf(p);
            const low = stock <= (p.min_stock || 5);
            return (
              <Card key={p.id} className="overflow-hidden transition-colors hover:border-primary/40" data-testid={`product-card-${p.sku}`}>
                <div className="relative aspect-[4/3] bg-secondary">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center"><Package className="h-8 w-8 text-muted-foreground" /></div>
                  )}
                  <Badge variant="outline" className="absolute left-2 top-2 bg-background/80 backdrop-blur">{p.season}</Badge>
                  <div className="absolute right-2 top-2 flex flex-col items-end gap-1">
                    {p.on_site != null && (p.on_site
                      ? <Badge variant="outline" className="border-emerald-600 bg-emerald-600 text-white shadow" data-testid={`product-site-${p.sku}`}>Attivo sul sito</Badge>
                      : <Badge variant="outline" className="border-zinc-600 bg-zinc-800 text-zinc-100 shadow" data-testid={`product-site-${p.sku}`}>{p.shop_status === "archived" ? "Archiviato" : p.shop_status === "draft" ? "Bozza · non sul sito" : p.shop_status === "unlisted" ? "Non in elenco sul sito" : p.shop_status === "active" ? "Attivo · non pubblicato sul negozio online" : "Non sul sito"}</Badge>)}
                    {low && p.shop_status !== "draft" && <Badge variant="outline" className="border-rose-500/30 bg-rose-500/15 text-rose-400">Sotto scorta</Badge>}
                  </div>
                </div>
                <div className="p-4">
                  <p className="font-mono-erp text-[11px] text-muted-foreground">{p.sku}</p>
                  <p className="font-medium leading-tight mt-0.5">{p.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{p.category} · {p.color}</p>
                  {p.care_symbols && <div className="mt-2"><CareSymbolsView value={p.care_symbols} className="h-5 w-5" testid={`care-${p.sku}`} /></div>}
                  <div className="mt-3 flex items-center justify-between">
                    <span className="font-display text-lg font-bold">{showMoney() && eur(p.price)}</span>
                    <span className={`text-sm font-semibold ${low ? "text-rose-400" : ""}`}>{num(stock)} pz</span>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-1.5 border-t border-border/50 pt-3">
                    {p.sizes.map((s) => (
                      <div key={s.size} className="flex items-center justify-center rounded bg-secondary px-1.5 py-1" data-testid={`size-${p.sku}-${s.size}`}>
                        <span className="font-mono-erp text-[11px]">{s.size}: {s.quantity}</span>
                      </div>
                    ))}
                  </div>
                  <ProducedQtyEdit product={p} />
                  <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => setChartProduct(p)} data-testid={`size-chart-btn-${p.sku}`}>
                    <Ruler className="mr-2 h-4 w-4" />Tabella misure
                  </Button>
                  <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => openBom(p)} data-testid={`bom-btn-${p.sku}`}>
                    <ListChecks className="mr-2 h-4 w-4" />
                    Distinta base {p.bom?.length ? `(${p.bom.length})` : ""}
                  </Button>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="ghost" size="sm" className="mt-2 w-full text-xs text-muted-foreground" data-testid={`history-btn-${p.sku}`}>
                        <History className="mr-2 h-3.5 w-3.5" />Storico modifiche
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-96 p-0"><EntityHistory entityId={p.id} model={p.name} testid={`product-history-${p.sku}`} /></PopoverContent>
                  </Popover>
                </div>
              </Card>
            );
          })}
          {filtered.length === 0 && <p className="col-span-full py-16 text-center text-muted-foreground">Nessun prodotto trovato. Sincronizza da Shopify per importare i tuoi prodotti finiti.</p>}
        </div>
      )}

      {!isLoading && filtered.length > 0 && (
        <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground" data-testid="products-pagination">
          <span data-testid="products-total">{num(filtered.length)} prodotti · pagina {page} di {Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => { setPage(1); window.scrollTo({ top: 0 }); }} data-testid="products-first-page">Prima</Button>
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => { setPage((p) => p - 1); window.scrollTo({ top: 0 }); }} data-testid="products-prev-page">
              <ChevronLeft className="h-4 w-4" />Precedente
            </Button>
            <Button variant="outline" size="sm" disabled={page * PAGE_SIZE >= filtered.length} onClick={() => { setPage((p) => p + 1); window.scrollTo({ top: 0 }); }} data-testid="products-next-page">
              Successiva<ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
        </TabsContent>

      <SizeChartDialog product={chartProduct} onClose={() => setChartProduct(null)} />
      {/* Shopify OAuth dialog */}
      <Dialog open={shopOpen} onOpenChange={setShopOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Collegamento Shopify (solo lettura)</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Il gestionale legge prodotti e ordini da Shopify tramite accesso sicuro (OAuth). Non scrive mai nulla sul tuo negozio.
            </p>
            {shopCfg?.connected ? (
              <div className="rounded-md border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-500" data-testid="shop-connected">
                Negozio collegato: {shopCfg.store_domain}
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>Dominio negozio</Label>
                <Input placeholder="xn4mnw-dh.myshopify.com" value={shopDomain} onChange={(e) => setShopDomain(e.target.value)} data-testid="shop-domain" />
                <p className="text-[11px] text-muted-foreground">Verrai reindirizzato a Shopify per autorizzare l'accesso in lettura a prodotti e ordini.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={connectShopify} data-testid="save-shop-btn">
              <Store className="mr-2 h-4 w-4" />{shopCfg?.connected ? "Riconnetti" : "Connetti con Shopify"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bill of materials dialog */}
      <Dialog open={!!bomProduct} onOpenChange={(o) => !o && setBomProduct(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto [&>*]:min-w-0">
          <DialogHeader><DialogTitle>Distinta base · {bomProduct?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Quantità di ogni materiale necessaria per <strong>un capo</strong>. Serve a calcolare il consumo nei lotti di produzione (dato gestionale, non su Shopify).</p>
          <BomEditor materials={materials} rows={bomRows} setRows={setBomRows} />
          <DialogFooter>
            <Button onClick={submitBom} disabled={saveBom.isPending} data-testid="save-bom-btn">
              {saveBom.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva distinta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
}
